import { useState, type ReactNode } from 'react'
import Button from '@mui/material/Button'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

/**
 * What a row can have done to it, in words, at rest.
 *
 * **A tooltip is not an affordance.** The routing tree carried six icon
 * buttons whose meaning lived entirely in `title`: nothing on a touch
 * device ever shows one, and a first-time reader with a mouse has to
 * hover six targets to find out what a row offers. Every one of those
 * tooltips was well written, which is what made the shape so easy to
 * keep.
 *
 * So the actions are a labelled menu. The trigger says a word, the items
 * say sentences, and the words are still the *intent's* — a caller
 * passes `actionLabel(name)` exactly as it did to the tooltips, because
 * the markers on a payload say that an intent names one placement and
 * cannot say whether naming it removes the placement or makes it the
 * home page.
 */
export interface RowAction {
  /** The item's words — the intent's own imperative, not a category. */
  label: string
  /** One line under it, where what the action does to the model is worth saying. */
  detail?: string
  icon?: ReactNode
  disabled?: boolean
  /** Drawn in the error colour: it takes something away. */
  destructive?: boolean
  onSelect: () => void
}

export function RowMenu({
  actions,
  label = 'Actions',
  disabled,
}: {
  actions: RowAction[]
  label?: string
  disabled?: boolean
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const offered = actions.filter((action) => action.label)

  if (offered.length === 0) return null

  return (
    <>
      <Button
        size="small"
        endIcon={<ExpandMoreIcon />}
        disabled={disabled}
        onClick={(event) => {
          // The rows these sit on are themselves selectable, and a click
          // on the menu is a click on the row unless it is stopped here.
          event.stopPropagation()
          setAnchor(event.currentTarget)
        }}
        sx={{ textTransform: 'none', flexShrink: 0 }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(event) => event.stopPropagation()}
        slotProps={{ paper: { sx: { maxWidth: 340 } } }}
      >
        {offered.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            onClick={() => {
              setAnchor(null)
              action.onSelect()
            }}
            sx={action.destructive ? { color: 'error.main' } : undefined}
          >
            {action.icon && <ListItemIcon sx={{ color: 'inherit' }}>{action.icon}</ListItemIcon>}
            <ListItemText
              primary={action.label}
              secondary={action.detail}
              slotProps={{ secondary: { sx: { whiteSpace: 'normal' } } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
